import { CommonModule } from "@angular/common";
import { Component, ViewChild, effect, inject } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { IonInput, IonicModule, ModalController, NavParams } from "@ionic/angular";
import { TranslateModule } from "@ngx-translate/core";

import { NewLoginService } from "@providers/NewLogin.service";
import { NewLoginAlertsService } from "@providers/NewLoginAlerts.service";
import { NewBaseService } from "@providers/newBase.service";

import { LicenseTypes } from "@models/MasterData";
import { GlobalVarsModel } from "@models/ModelGlobalsVar";

const EVENTS_TOOLS = "eventstrackingtesttool";

export enum Steps {
  Login = 1,
  GetPassword = 2,
}

interface LoginForm {
  username: FormControl<string>;
  password: FormControl<string>;
  rememberMe: FormControl<boolean>;
}

interface ResetPasswordForm {
  user: FormControl<string>;
  captcha: FormControl<string>;
}

@Component({
  selector: "codere-new-login",
  standalone: true,
  imports: [CommonModule, IonicModule, ReactiveFormsModule, TranslateModule],
  templateUrl: "./new-login.component.html",
  styleUrls: ["./new-login.component.scss"],
})
export class NewLoginComponent {
  @ViewChild("userInput", { static: false }) userInput: IonInput;

  public activeStep: Steps = Steps.Login;
  public allSteps = Steps;
  public globalVars!: GlobalVarsModel;
  public loginForm: FormGroup<LoginForm>;
  public resetForm: FormGroup<ResetPasswordForm>;
  public showPassword = false;
  public typePassword = "password";
  private footerLastState: boolean;

  private newBaseService = inject(NewBaseService);
  public newLoginService = inject(NewLoginService);
  public newLoginAlertsService = inject(NewLoginAlertsService);
  private navParams = inject(NavParams);
  private modalCtrl = inject(ModalController);

  constructor() {
    this.newBaseService.getVars.subscribe((data) => {
      this.globalVars = data;
    });

    this.globalVars.loginOpen = true;
    this.activeStep = this.navParams?.get("activeSection") ? this.navParams?.get("activeSection") : Steps.Login;

    this.setForms();

    if (this.activeStep === Steps.GetPassword) {
      this.newLoginAlertsService.showAlertPreSignup();
    }

    effect(() => {
      if (this.modalCtrl) {
        this.modalCtrl.getTop().then((loginAlert) => {
          if (loginAlert) {
            loginAlert.backdropDismiss = !this.newLoginService.isGeolocating();
          }
        });
      }

      if (this.loginForm) {
        if (this.newLoginService.isGeolocating()) {
          this.loginForm.controls.password.disable();
          this.loginForm.controls.username.disable();
        } else {
          this.loginForm.controls.password.enable();
          this.loginForm.controls.username.enable();
        }
      }
    });
  }

  setForms() {
    this.loginForm = new FormGroup({
      username: new FormControl(this.newLoginService.getUserName, [Validators.required]),
      password: new FormControl("", [Validators.required]),
      rememberMe: new FormControl(!this.globalVars.FEATURES.MSO_SkipAutologin),
    });

    const validatorsCaptcha = !this.globalVars.FEATURES.MSO_LoginCaptchaDisabled ? [Validators.required] : [];

    this.resetForm = new FormGroup({
      user: new FormControl(this.newLoginService.getUserName, [Validators.required]),
      captcha: new FormControl("", validatorsCaptcha),
    });

    this.resetForm.controls.captcha.valueChanges.subscribe({
      next: (captcha) => {
        this.resetForm.controls.captcha.setValue(this.onlyNumbers(captcha), { emitEvent: false });
      },
    });
  }

  ionViewDidEnter() {
    if (this.globalVars.FEATURES.MSO_alertCookiesNoBlock) {
      if (!this.globalVars.isCookiesAccept) {
        if (this.globalVars.licenseType === LicenseTypes.Nacional) {
          this.globalVars.rootScope.alertCookies.dismiss();
          this.globalVars.rootScope.openCookiesConsent();
        } else {
          this.globalVars.rootScope.openAlertCookies();
        }

        this.closeLogin();

        return;
      }
    }

    this.newLoginService.setLoginComponent = this;

    if (this.globalVars.gotorecovery) {
      this.gotoStep(Steps.GetPassword);
      this.globalVars.gotorecovery = false;
    } else {
      this.userInput?.setFocus();
    }
  }

  ionViewWillEnter() {
    this.footerLastState = this.globalVars.hideBetFooterBar;
    this.globalVars.hideBetFooterBar = true;
  }

  ionViewDidLeave() {
    this.globalVars.hideBetFooterBar = this.footerLastState;
    this.globalVars.loginOpen = false;
    this.newLoginService.removeLoginComponent();
    this.newLoginService.isGeolocating.set(false);
  }

  doLogin() {
    if (this.loginForm.valid && !this.newLoginService.isGeolocating()) {
      const { password, rememberMe, username } = this.loginForm.getRawValue();
      this.newLoginService.doLogin(username, password, rememberMe);
    } else {
      this.newLoginAlertsService.invalidLoginForm();
    }
  }

  doGetPassword() {
    if (this.resetForm.valid) {
      if (this.newLoginService.canGetPassword()) {
        const { user, captcha } = this.resetForm.getRawValue();
        this.newLoginService.getNewPassword(user, captcha).then((reseted) => {
          if (reseted) {
            this.activeStep = Steps.Login;
            this.resetForm.reset();
          }
        });
      } else {
        this.newLoginAlertsService.showAlertResetPasswordOk().then(() => {
          this.activeStep = Steps.Login;
          this.resetForm.reset();
        });
      }
    } else {
      if (
        !!this.resetForm.controls.user.value &&
        this.resetForm.controls.user.value.toLocaleLowerCase() === EVENTS_TOOLS
      ) {
        this.newLoginService.eventsTrackTools();
        this.closeLogin();
      } else {
        this.newLoginAlertsService.showAlertInvalidFormCaptcha();
      }
    }
  }

  closeLogin() {
    if (this.modalCtrl && !this.newLoginService.isGeolocating()) {
      this.modalCtrl.dismiss();
    }
  }

  gotoStep(step: Steps) {
    this.activeStep = step;
    if (step === Steps.GetPassword && !this.globalVars.FEATURES.MSO_LoginCaptchaDisabled) {
      this.newCaptcha();
    }
  }

  openRegistro() {
    if (this.newLoginService.isGeolocating()) {
      return;
    }
    this.modalCtrl.dismiss(null, null).then(() => {
      this.globalVars.rootScope.openRegisterByLicense();
    });
  }

  onClickShowPassword() {
    this.showPassword = !this.showPassword;
    if (this.showPassword) {
      this.typePassword = "text";
    } else {
      this.typePassword = "password";
    }
  }

  newCaptcha() {
    this.newLoginService.newCaptchaUrl();
  }

  onlyNumbers(captcha: string): string {
    return captcha.replace(/[^0-9]/g, "");
  }
}
